import React, { useState } from 'react';

import ActionButton from '~components/atoms/ActionButton';
import Image from '~components/atoms/Image';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';
import Table from '~components/molecules/Table';

import { useAppSelector } from '~redux/hooks';
import { GoldSilverInventorySelectedTab } from '~types/redux/state';
import { FundTypeEnum, GoldTypeEnum, SilverTypeEnum } from '~utils/enums';

const InventoryListingTable = () => {
    const getInventoryListingAttempt = useAppSelector(state => state.gold.loadings.getInventoryListing);

    const getInventoryListingFailure = useAppSelector(state => state.gold.errors.getInventoryListing);

    const inventoryListing = useAppSelector(state => state.gold.goldInventory.inventoryListing.data);

    const selectedInventoryTab = useAppSelector(state => state.gold.goldInventory.inventoryListing.selectedTab);

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    const tableHeaderForPhysical = [
        'IMAGE',
        'PRODUCT NAME',
        'BRANCH NAME',
        'QUANTITY',
        'SERIAL NUMBER',
        'TOTAL WEIGHT (g)',
        'FUND TYPE',
    ];

    const tableHeaderForPaper = [
        'IMAGE',
        'PRODUCT NAME',
        'BRANCH NAME',
        'SERIAL NUMBER',
        'TOTAL WEIGHT (g)',
        'FUND TYPE',
    ];

    const isPhysicalTab = [
        GoldSilverInventorySelectedTab.GoldIslamicPhysical,
        GoldSilverInventorySelectedTab.GoldConventionalPhysical,
        GoldSilverInventorySelectedTab.SilverIslamicPhysical,
        GoldSilverInventorySelectedTab.SilverConventionalPhysical,
    ].includes(selectedInventoryTab);

    const tableBody = (inventoryListing || [])
        .filter(product => {
            const { fundType } = product;

            const isGold = product.goldType !== undefined && product.goldType !== null;

            const isSilver =
                product.silverType !== undefined &&
                product.silverType !== null;

            if (isGold) {
                const { goldType } = product;

                if (selectedInventoryTab === GoldSilverInventorySelectedTab.GoldIslamicPaper) {
                    return (
                        goldType === GoldTypeEnum.PaperGold &&
                        fundType === FundTypeEnum.Islamic
                    );
                }

                if (selectedInventoryTab === GoldSilverInventorySelectedTab.GoldIslamicPhysical) {
                    return (
                        goldType === GoldTypeEnum.PhysicalGold &&
                        fundType === FundTypeEnum.Islamic
                    );
                }

                if (selectedInventoryTab === GoldSilverInventorySelectedTab.GoldConventionalPaper) {
                    return (
                        goldType === GoldTypeEnum.PaperGold &&
                        fundType === FundTypeEnum.Conventional
                    );
                }

                if (
                    selectedInventoryTab ===
                    GoldSilverInventorySelectedTab.GoldConventionalPhysical
                ) {
                    return (
                        goldType === GoldTypeEnum.PhysicalGold &&
                        fundType === FundTypeEnum.Conventional
                    );
                }
            }

            if (isSilver) {
                const { silverType } = product;

                if (selectedInventoryTab === GoldSilverInventorySelectedTab.SilverIslamicPaper) {
                    return (
                        silverType === SilverTypeEnum.PaperSilver &&
                        fundType === FundTypeEnum.Islamic
                    );
                }

                if (selectedInventoryTab === GoldSilverInventorySelectedTab.SilverIslamicPhysical) {
                    return (
                        silverType === SilverTypeEnum.PhysicalSilver &&
                        fundType === FundTypeEnum.Islamic
                    );
                }

                if (selectedInventoryTab === GoldSilverInventorySelectedTab.SilverConventionalPaper) {
                    return (
                        silverType === SilverTypeEnum.PaperSilver &&
                        fundType === FundTypeEnum.Conventional
                    );
                }

                if (selectedInventoryTab === GoldSilverInventorySelectedTab.SilverConventionalPhysical) {
                    return (
                        silverType === SilverTypeEnum.PhysicalSilver &&
                        fundType === FundTypeEnum.Conventional
                    );
                }
            }

            return false;
        })
        .map(transaction => {
            const {
                inventoryId,
                image,
                productName,
                branchName,
                serialNo,
                fundType,
                amount,
            } = transaction;

            const isGold = [
                GoldSilverInventorySelectedTab.GoldIslamicPaper,
                GoldSilverInventorySelectedTab.GoldIslamicPhysical,
                GoldSilverInventorySelectedTab.GoldConventionalPaper,
                GoldSilverInventorySelectedTab.GoldConventionalPhysical,
            ].includes(selectedInventoryTab);

            const handleViewSerialNo = () => {
                if (!serialNo) return;

                setIsSerialNoModalOpen(true);
                setSerialNoList(serialNo);
            };

            const renderSerialNo = () => {
                if (!serialNo) return '-';

                return (
                    <ActionButton
                        key={inventoryId}
                        label='View'
                        onClick={handleViewSerialNo}
                    />
                );
            };

            const isPhysical = isGold
                ? transaction.goldType === GoldTypeEnum.PhysicalGold
                : transaction.silverType === SilverTypeEnum.PhysicalSilver;

            const weight = isGold
                ? (
                    isPhysical
                        ? transaction.physicalGoldWeight
                        : transaction.paperGoldWeight
                )
                : (
                    isPhysical
                        ? transaction.physicalSilverWeight
                        : transaction.paperSilverWeight
                );

            if (isPhysical) {
                return [
                    <Image
                        key={inventoryId}
                        src={image || ''}
                        width={40}
                        height={40}
                    />,
                    productName,
                    branchName,
                    amount,
                    renderSerialNo(),
                    typeof weight === 'number' ? `${weight.toFixed(3)} g` : '-',
                    FundTypeEnum[fundType] || '-',
                ];
            }

            return [
                <Image
                    key={inventoryId}
                    src={image || ''}
                    width={40}
                    height={40}
                />,
                productName,
                branchName,
                renderSerialNo(),
                typeof weight === 'number' ? `${weight.toFixed(3)} g` : '-',
                FundTypeEnum[fundType] || '-',
            ];
        });

    return (
        <>
            <Table
                header={isPhysicalTab ? tableHeaderForPhysical : tableHeaderForPaper}
                body={tableBody}
                isLoading={getInventoryListingAttempt}
                error={getInventoryListingFailure}
            />

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />
        </>
    );
};

export default InventoryListingTable;
